import { create } from 'zustand';
import { Order, MainWorkOrder, SubWorkOrder, RushOrder, ProductionReport as ProductionReportType } from '@/types';

interface OrderState {
  orders: Order[];
  mainWorkOrders: MainWorkOrder[];
  subWorkOrders: SubWorkOrder[];
  rushOrders: RushOrder[];
  productionReports: ProductionReportType[];

  // Actions
  setOrders: (orders: Order[]) => void;
  setMainWorkOrders: (orders: MainWorkOrder[]) => void;
  setSubWorkOrders: (orders: SubWorkOrder[]) => void;
  setRushOrders: (orders: RushOrder[]) => void;

  addOrder: (order: Order) => void;
  updateOrder: (id: string, updates: Partial<Order>) => void;
  deleteOrder: (id: string) => void;

  addMainWorkOrder: (order: MainWorkOrder) => void;
  updateMainWorkOrder: (id: string, updates: Partial<MainWorkOrder>) => void;

  addSubWorkOrder: (order: SubWorkOrder) => void;
  updateSubWorkOrder: (id: string, updates: Partial<SubWorkOrder>) => void;

  addRushOrder: (order: RushOrder) => void;
  updateRushOrder: (id: string, updates: Partial<RushOrder>) => void;

  addProductionReport: (report: ProductionReportType) => void;
  getTodayReportsCount: () => number;
  getProductionReportsBySubOrderId: (subOrderId: string) => ProductionReportType[];

  // Getters
  getOrderById: (id: string) => Order | undefined;
  getMainWorkOrdersByOrderId: (orderId: string) => MainWorkOrder[];
  getSubWorkOrdersByMainOrderId: (mainOrderId: string) => SubWorkOrder[];
  getRushOrderById: (id: string) => RushOrder | undefined;
}

export const useOrderStore = create<OrderState>((set, get) => ({
  // Initial state
  orders: [],
  mainWorkOrders: [],
  subWorkOrders: [],
  rushOrders: [],
  productionReports: [],

  // Setters
  setOrders: (orders) => set({ orders }),
  setMainWorkOrders: (orders) => set({ mainWorkOrders: orders }),
  setSubWorkOrders: (orders) => set({ subWorkOrders: orders }),
  setRushOrders: (orders) => set({ rushOrders: orders }),
  setProductionReports: (reports) => set({ productionReports: reports }),

  // Order actions
  addOrder: (order) => set((state) => ({ orders: [...state.orders, order] })),

  updateOrder: (id, updates) =>
    set((state) => ({
      orders: state.orders.map((order) =>
        order.id === id ? { ...order, ...updates } : order
      ),
    })),

  deleteOrder: (id) =>
    set((state) => ({
      orders: state.orders.filter((order) => order.id !== id),
    })),

  // MainWorkOrder actions
  addMainWorkOrder: (order) =>
    set((state) => ({ mainWorkOrders: [...state.mainWorkOrders, order] })),

  updateMainWorkOrder: (id, updates) =>
    set((state) => ({
      mainWorkOrders: state.mainWorkOrders.map((order) =>
        order.id === id ? { ...order, ...updates } : order
      ),
    })),

  // SubWorkOrder actions
  addSubWorkOrder: (order) =>
    set((state) => ({ subWorkOrders: [...state.subWorkOrders, order] })),

  updateSubWorkOrder: (id, updates) =>
    set((state) => ({
      subWorkOrders: state.subWorkOrders.map((order) =>
        order.id === id ? { ...order, ...updates } : order
      ),
    })),

  // RushOrder actions
  addRushOrder: (order) =>
    set((state) => ({ rushOrders: [...state.rushOrders, order] })),

  updateRushOrder: (id, updates) =>
    set((state) => ({
      rushOrders: state.rushOrders.map((order) =>
        order.id === id ? { ...order, ...updates } : order
      ),
    })),

  // Getters
  getOrderById: (id) => get().orders.find((order) => order.id === id),

  getMainWorkOrdersByOrderId: (orderId) =>
    get().mainWorkOrders.filter((order) => order.orderId === orderId),

  getSubWorkOrdersByMainOrderId: (mainOrderId) =>
    get().subWorkOrders.filter((order) => order.mainOrderId === mainOrderId),

  getRushOrderById: (id) => get().rushOrders.find((order) => order.id === id),

  // Production report actions
  addProductionReport: (report) =>
    set((state) => {
      // 同時更新生產報工記錄和子工單的實際產出數量
      const updatedSubOrders = state.subWorkOrders.map((order) =>
        order.id === report.subWorkOrderId
          ? { ...order, actualQuantity: order.actualQuantity + report.goodQuantity }
          : order
      );

      return {
        productionReports: [...state.productionReports, report],
        subWorkOrders: updatedSubOrders,
      };
    }),

  getTodayReportsCount: () => {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    return get().productionReports.filter((report) =>
      report.reportDate.startsWith(today)
    ).length;
  },

  getProductionReportsBySubOrderId: (subOrderId) =>
    get().productionReports.filter((report) => report.subWorkOrderId === subOrderId),
}));
